import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { ID } from '../../../../../../../types';
import {
  UniversalFilterUsersExtendedValue,
  UniversalFilterUsersExtendedModes,
  UniversalFilterUsersExtendedFilterState,
  UniversalFilterUsersExtendedOnFilterStateChange,
  UniversalFilterUsersExtendedOnChange
} from '../../UniversalFilterUsersExtended.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterUsersExtendedFilterState } from '../useUniversalFilterUsersExtendedFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterUsersExtendedModes.CONTAINS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterUsersExtendedModes>[] =
  [
    {
      value: UniversalFilterUsersExtendedModes.CONTAINS_ANY_OF,
      i18nText: filtersKeys.modes.containsAnyOf
    },
    // {
    //   value: UniversalFilterUsersExtendedModes.CONTAINS_ALL_OF,
    //   i18nText: filtersKeys.modes.containsAllOf
    // },
    // {
    //   value: UniversalFilterUsersExtendedModes.CONTAINS_ONLY,
    //   i18nText: filtersKeys.modes.containsOnly
    // },
    // {
    //   value: UniversalFilterUsersExtendedModes.CONTAINS_NONE_OF,
    //   i18nText: filtersKeys.modes.containsNoneOf
    // },
    {
      value: UniversalFilterUsersExtendedModes.DOES_NOT_CONTAIN_ANY_OF,
      i18nText: filtersKeys.modes.doesNotContainAnyOf
    }
    // {
    //   value: UniversalFilterUsersExtendedModes.IS_EMPTY,
    //   i18nText: filtersKeys.modes.isEmpty
    // },
    // {
    //   value: UniversalFilterUsersExtendedModes.IS_NOT_EMPTY,
    //   i18nText: filtersKeys.modes.isNotEmpty
    // }
  ];

interface UniversalFilterUsersExtendedOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterUsersExtendedValue;
  onChange: UniversalFilterUsersExtendedOnChange;
}

function useUniversalFilterUsersExtended({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterUsersExtendedOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterUsersExtendedFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterUsersExtendedFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterUsersExtendedFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          userIds: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterUsersExtendedOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterUsersExtendedModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        userIds: filterState.userIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleUserIdsChange = useCallback<(userIds: ID[]) => void>(
    (userIds) => {
      handleFilterStateChange({
        mode: filterState.mode,
        userIds: userIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleUserIdsChange
  };
}

export default useUniversalFilterUsersExtended;
